import { useEffect, useMemo, useState } from 'react';

import { Box, Skeleton } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { setFiltersAction, setTeamAction } from 'actions/globalFilters';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import TeamModel from 'models/team';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { INSIGHT_VIEWS, useOKROwnership } from 'reducers/okrsPageSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { getCompletedOnDurationFromFilters } from 'screens/Initiatives/helpers';
import { getDefaultLocalStorageStatsFilters, useTeamPageState } from 'screens/Team';
import { GoalsTreeViewTable } from 'screens/Team/Rabbit/Goals/AgGridTreeView/GoalsTreeViewTable';
import { GroupByGoalGraphs } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/Graphs/GroupByGoal/GroupByGoalGraphs';
import { GroupByIndividualGraphs } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/Graphs/GroupByIndividual/GroupByIndividualGraphs';
import { GroupByGoalMetricsCards } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/MetricsCards/GroupByGoal/GroupByGoalMetricsCards';
import { GroupByIndividualMetricsCards } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/MetricsCards/GroupByIndividual/GroupByIndividualMetricsCards';
import { goalsCascadedViewReactQueryKey } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/dataHooks';
import { useGroupByGoalsData } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useGroupByGoalsData';
import { useGroupByIndividualData } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useGroupByIndividualData';
import { useOKRGroupByViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRGroupByViewType';
import { useOKRManageViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRManageViewType';
import OKRCardView from 'screens/Team/Rabbit/Goals/Card';
import { GROUP_BY_INDIVIDUAL_KEY_OBJ, SPLIT_BY_NONE_KEY_OBJ } from 'screens/Team/Rabbit/Goals/Insights';
import GroupByIndividualInsightsTable from 'screens/Team/Rabbit/Goals/Insights/RabbitGroupByIndividualInsightsTable';
import TreeView from 'screens/Team/Rabbit/Goals/Tree/RabbitTreeView';
import buildTeamPageFilters from 'screens/Team/TeamPageFilters/buildTeamPageFilters';
import { okrsPageSelector } from 'selectors/okrsPage';
import { baseTheme } from 'themes/clients/baseTheme';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE, TASK_TYPES_2 } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';
import { routes } from 'constants/routes';

import { GoalsIntentAgGridMenu } from './components/GoalsIntentAgGridMenu';

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

const generateAttributeFilter = (activeFilters = []) => {
  const data = activeFilters.map(({ value }) => value);
  return {
    type: 'STATUS',
    operatorType: 'IN',
    data,
  };
};

export const useGoalsManageGridStatePersistenceKey = ({ showParentObjectives = false, viewId = '' }) => {
  const sideNavSelectedCohort = useSelectedView();
  const viewId1 = viewId !== '' ? viewId : sideNavSelectedCohort?.id;

  const { groupByViewType } = useOKRGroupByViewType();

  const isGroupByGoals = groupByViewType === INSIGHT_VIEWS[1].value;
  // const isGroupByIndividual = groupByViewType === INSIGHT_VIEWS[0].value;

  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
    const tabId = isGroupByGoals ? 'Explore' : 'Insights';
    const uniqueIdGroupByGoal = showParentObjectives ? 'ExploreGrid' : 'ListGrid';
    const uniqueIdGroupByIndividual = 'GroupByIndividualGrid';

    return getPersistenceKey({
      moduleName,
      viewId: viewId1,
      tabId,
      uniqueId: isGroupByGoals ? uniqueIdGroupByGoal : uniqueIdGroupByIndividual,
    });
  }, [isGroupByGoals, showParentObjectives, viewId1]);

  return gridStatePersistenceKey;
};

const GoalsIntentLandingPage = ({ showGraphs = true, showMetrics = true, gridRef }) => {
  const sideNavSelectedCohort = useSelectedView();
  const { selectedTimelines } = useSelector(okrsPageSelector);
  const ownership = useOKROwnership();

  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);

  const location = useLocation();

  // const isHierarchyView = checkNewNavEnabled() ? isShowParentObjectives : Boolean(isAuditRoute);

  const {
    cardKPIData: groupByGoalCardKPIData,
    chartData: groupByGoalChartData,
    isLoading: groupByGoalIsLoading,
    setChartData: setGroupByGoalChartData,
    onModalUpdated: onGroupByGoalModalUpdated,
    donutChartData: groupByGoalDonutChartData,
    setRowData: groupByGoalSetRowData,
  } = useGroupByGoalsData({
    gridRef,
  });

  const {
    cardKPIData: groupByIndividualCardKPIData,
    chartData: groupByIndividualGoalsData,
    isLoading: groupByIndividualIsLoading,
    setChartData: setGroupByIndividualChartData,
    onModalUpdated: onGroupByIndividualModalUpdated,
  } = useGroupByIndividualData({
    gridRef,
  });

  /*  read keyResultsName and objectivesName from new config */
  const { keyResultsName } = useOrganisationSettings();

  const { id: paramsTeamId, name: teamName } = sideNavSelectedCohort ?? {};
  const dispatch = useDispatch();

  // const { viewType: groupByType } = useOKRInsights();
  const { groupByViewType } = useOKRGroupByViewType();

  const isGroupByGoals = groupByViewType === INSIGHT_VIEWS[1].value;
  const isGroupByIndividual = groupByViewType === INSIGHT_VIEWS[0].value;

  const gridStatePersistenceKey = useGoalsManageGridStatePersistenceKey({});

  const {
    team,
    setTeam,
    activeFilters,
    refreshData,
    loggedInEmployee,
    selectedTaskType,
    activeFiltersDispatch,
    newFiltersState,
    setRefreshData,
  } = useTeamPageState({
    sideNavSelectedCohort,
  });

  const attributeFilter = generateAttributeFilter(activeFilters);

  const [, setDuration] = useState(getCompletedOnDurationFromFilters(newFiltersState));

  const { viewType: OKRManageView, replaceViewType, setViewType } = useOKRManageViewType();

  const isHierarchyView = OKRManageView === 'listWithHierarchy';

  useEffect(() => {
    if (!OKRManageView) {
      replaceViewType('list');
    }
  }, [OKRManageView, replaceViewType]);

  useEffect(() => {
    // when sideNavSelectedCohort changes, reset the view type to list
    if (OKRManageView !== 'list' && !checkNewNavEnabled()) {
      setViewType('list');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideNavSelectedCohort?.id, checkNewNavEnabled]);

  // tree view state
  const [, setTimeboundDetailsOfCreatedObjective] = useState(null);
  const [isOpenCreateGoalTreeViewPanel, setIsOpenCreateGoalTreeViewPanel] = useState(false);

  const [, setIsGoalPanelOfCreatedObjective] = useState(false);

  const refresh = () => {
    setRefreshData(true);
    setTimeout(() => {
      setRefreshData(false);
    }, 100);
  };

  const queryClient = useQueryClient();

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries(
      goalsCascadedViewReactQueryKey({
        cohort: sideNavSelectedCohort,
        groupByKeys: GROUP_BY,
        splitByKeys: SPLIT_BY,
        orderBy: null,
        timeBounds: [selectedTimelines?.[0]],
        attributeFilter,
        isHierarchyView,
        pageType: 'TEAM_HUB',
        ownership,
      })
    );
  };

  useEffect(() => {
    const teamId = paramsTeamId;
    if (!teamId) return;

    dispatch(setTeamAction(teamId)).then(() => {
      setTeam(new TeamModel({ id: teamId, name: teamName }));
    });
  }, [dispatch, setTeam, paramsTeamId, teamName]);

  useEffect(() => {
    const newActiveFilters = buildTeamPageFilters(newFiltersState, true);
    setDuration((duration) => {
      const newDuration = getCompletedOnDurationFromFilters(newFiltersState);
      return JSON.stringify(duration) !== JSON.stringify(newDuration) ? newDuration : duration;
    });
    const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(TASK_TYPES_2.goal) || [];
    activeFiltersDispatch(setFiltersAction([...newActiveFilters, ...localStorageStatsFilters]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(newFiltersState)]);

  if (!sideNavSelectedCohort || !team || !team?.id) {
    return <Skeleton height="30vh" />;
  }

  const showParentObjectives = Boolean(
    matchPath(location.pathname, {
      path: routes.goals.audit,
      exact: false,
    })
  );

  const showChartsAndMetrics = OKRManageView === 'list' || OKRManageView === 'listWithHierarchy';

  return (
    <>
      {isGroupByGoals && showChartsAndMetrics ? (
        <>
          <GroupByGoalGraphs
            chartData={groupByGoalChartData}
            showGraphs={showGraphs}
            donutChartData={groupByGoalDonutChartData}
          />
          <Box my={3}>
            <GroupByGoalMetricsCards
              cardKPIData={groupByGoalCardKPIData}
              isLoading={groupByGoalIsLoading}
              showMetrics={showMetrics}
              gridStatePersistenceKey={gridStatePersistenceKey}
            />
          </Box>
        </>
      ) : null}
      {isGroupByIndividual && showChartsAndMetrics && showGraphs ? (
        <>
          <GroupByIndividualGraphs
            goalsOwnedChartData={groupByIndividualGoalsData}
            gridRef={gridRef}
            keyResultsName={keyResultsName}
          />
          <Box mt={3}>
            <GroupByIndividualMetricsCards
              cardKPIData={groupByIndividualCardKPIData}
              isLoading={groupByIndividualIsLoading}
              gridStatePersistenceKey={gridStatePersistenceKey}
            />
          </Box>
        </>
      ) : null}
      <Box width="100%" mt={3}>
        <Box
          background="white"
          p={3}
          paddingBottom="0px"
          width="100%"
          borderRadius="8px"
          boxShadow={baseTheme.shadows.paper}
        >
          <GoalsIntentAgGridMenu
            gridRef={gridRef}
            refetchCascadedViewData={refetchCascadedViewData}
            selectedTimelines={selectedTimelines}
          />
          {OKRManageView === 'list' || OKRManageView === 'listWithHierarchy' ? (
            <>
              {isGroupByGoals && (
                <GoalsTreeViewTable
                  showParentObjectives={showParentObjectives}
                  attributeFilter={attributeFilter}
                  timeBoundPeriod={selectedTimelines?.[0] ?? null}
                  type={selectedTaskType}
                  isHierarchyView={isHierarchyView}
                  gridRef={gridRef}
                  setChartData={setGroupByGoalChartData}
                  setRowData={groupByGoalSetRowData}
                  onModalUpdated={onGroupByGoalModalUpdated}
                  ownership={ownership}
                />
              )}

              {isGroupByIndividual && (
                <GroupByIndividualInsightsTable
                  selectedCohort={sideNavSelectedCohort}
                  gridRef={gridRef}
                  selectedTimeBoundPeriods={selectedTimelines}
                  selectedGroupByKey={GROUP_BY_INDIVIDUAL_KEY_OBJ}
                  selectedSplitByKey={SPLIT_BY_NONE_KEY_OBJ}
                  setChartData={setGroupByIndividualChartData}
                  onModalUpdated={onGroupByIndividualModalUpdated}
                />
              )}
            </>
          ) : null}

          {OKRManageView === 'card' ? <OKRCardView /> : null}

          {OKRManageView === 'tree' ? (
            <TreeView
              isOpenCreateGoalPanel={isOpenCreateGoalTreeViewPanel}
              setIsOpenCreateGoalPanel={setIsOpenCreateGoalTreeViewPanel}
              employee={loggedInEmployee}
              selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
              refreshData={refreshData}
              refreshFn={refresh}
              teamId={team.id}
              isOpenCreateObjectivePanel={isOpenCreateObjectivePanel}
              setIsOpenCreateObjectivePanel={setIsOpenCreateObjectivePanel}
              selectedCohort={sideNavSelectedCohort}
              setIsGoalPanelOfCreatedObjective={setIsGoalPanelOfCreatedObjective}
              setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
            />
          ) : null}
        </Box>
      </Box>
    </>
  );
};

export { GoalsIntentLandingPage };
